import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useMemo } from 'react';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Empty from '@/components/Empty/Empty';
import List from '@/components/List/List';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import {
  type Issue,
  issueListQueryOptions,
  useHideIssueMutation,
} from '@/entities/issue';
import { cn } from '@/lib/cn';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

export const HiddenIssueListPane = ({ onClose }: { onClose: () => void }) => {
  const wa = getCurrWorkspaceAlias();
  const { data: originIssues = [] } = useQuery(issueListQueryOptions({ workspaceId: wa }));
  const hideIssueMutation = useHideIssueMutation();

  const hiddenIssues = useMemo(() => {
    return originIssues.filter((issue) => issue.hidden);
  }, [originIssues]);

  const onUnhide = async (issueName: string) => {
    await hideIssueMutation.mutateAsync({ name: issueName, hidden: false });
  };

  return (
    <VFlex className="w-full h-screen bg-white overflow-auto">
      {/* Header */}
      <Flex a="center" j="space-between" className="px-6 py-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-gray-900">Hidden issues</h2>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            icon={<X className="size-4" />}
            sizeType="m"
            onClick={onClose}
          />
        </div>
      </Flex>

      {/* Hidden Issue List */}
      <ScrollArea className="flex-1 overflow-auto size-full">
        <List
          className="h-full"
          data={hiddenIssues}
          placeholder={{
            node: (
              <Empty
                title="No hidden issues"
                description="Hidden issues will appear here"
              />
            ),
          }}
          render={(issue) => (
            <HiddenIssueItem
              key={issue.name}
              issue={issue}
              onUnhide={onUnhide}
            />
          )}
        />
      </ScrollArea>
    </VFlex>
  );
};

const HiddenIssueItem = ({
  issue,
  onUnhide,
}: {
  issue: Issue;
  onUnhide: (issueName: string) => void;
}) => {
  return (
    <div
      key={issue.name}
      className="border-b-1 border-gray-200 last:border-b-0"
    >
      <div
        className={cn(
          'px-6 py-4 cursor-pointer transition-colors hover:bg-gray-50 flex items-center justify-between gap-4',
        )}
      >
        <span className="text-gray-900 text-sm font-medium line-clamp-1">
          {issue.name}
        </span>

        <Button
          variant="ghost"
          sizeType="m"
          onClick={() => onUnhide(issue.name)}
        >
          Unhide
        </Button>
      </div>
    </div>
  );
};
